/* @flow */

import * as React from 'react';
import { isFunction } from 'lodash';
import ReactDOM from 'react-dom';
import { TimelineMax } from 'gsap';

type ContextOptions = {
  anyLeaving: boolean,
  anyEntering: boolean
};

type Props = {
  anyLeaving?: boolean,
  anyEntering?: boolean,
  children: any,
  isEntering?: boolean,
  isLeaving?: boolean,
  onAppear?: (() => (node: any, contextOptions: ContextOptions) => TimelineMax) | TimelineMax,
  onEnter?: (() => (node: any, contextOptions: ContextOptions) => TimelineMax) | TimelineMax,
  onLeave?: (() => (node: any, contextOptions: ContextOptions) => TimelineMax) | TimelineMax
};

class AnimatedGroupChild extends React.Component {
  props: Props;

  static defaultProps = {
    onAppear: undefined,
    onEnter: undefined,
    onLeave: undefined,
    anyLeaving: false,
    anyEntering: false,
    isEntering: false,
    isLeaving: false
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isEntering, isLeaving } = this.props;
    const isNowLeaving = isEntering && !nextProps.isEntering && nextProps.isLeaving;
    const isNowEntering = isLeaving && !nextProps.isLeaving && nextProps.isEntering;

    if (isNowLeaving || isNowEntering) {
      this.killTimeline();
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.killTimeline();
  }

  isUnmounted = false;

  runningTimeline: {
    onComplete: () => void,
    timeline: TimelineMax
  };

  killTimeline() {
    if (this.runningTimeline) {
      // kill it
      this.runningTimeline.timeline.kill();
      // then clean it up and let the transition group know
      // it can render it or remove it
      this.runningTimeline.onComplete();
    }
  }

  /** creates the base animations for this child from what's passed in */
  bootstrapAnimation(animatedProps, callback, contextInfo = {}) {
    // kill anything currently running, we're going to run something else
    this.killTimeline();

    const timelineObj = {};
    const animationDoneCallback = () => {
      this.runningTimeline = undefined;
      // the components already gone away, no need to make the call back to let it know
      // the animation we're doing here was done
      // this prevents errors caused by calling it after it's unmounted as it
      // tries to do things on the component still
      if (!this.isUnmounted) {
        callback();
      }
    };

    let timeline;
    if (isFunction(animatedProps)) {
      if (!this.node) {
        animationDoneCallback();
        return;
      }

      const realNode =
        this.node && this.node.current ? this.node.current : ReactDOM.findDOMNode(this.node);
      if (!realNode) {
        animationDoneCallback();
        return;
      }

      timeline = animatedProps(realNode, contextInfo);
    } else if (animatedProps instanceof TimelineMax) {
      timeline = animatedProps;
    } else {
      animationDoneCallback();
      return;
    }

    // React is waiting for the callback to be called to know that our event is done
    // so make sure it's called on onComplete
    timeline.vars.onComplete = animationDoneCallback;

    timelineObj.timeline = timeline;
    timelineObj.onComplete = animationDoneCallback;
    this.runningTimeline = timelineObj;

    timeline.play();
  }

  componentWillAppear(callback) {
    const { onAppear } = this.props;
    const animatedProps = onAppear;
    if (animatedProps) {
      this.bootstrapAnimation(animatedProps, callback);
    } else {
      callback();
    }
  }

  componentWillEnter(callback) {
    const { anyLeaving, onEnter } = this.props;
    const contextInfo = { anyLeaving };
    const animatedProps = onEnter;
    if (animatedProps) {
      this.bootstrapAnimation(animatedProps, callback, contextInfo);
    } else {
      callback();
    }
  }

  componentWillLeave(callback) {
    const { anyEntering, onLeave } = this.props;
    const contextInfo = { anyEntering };
    const animatedProps = onLeave;
    if (animatedProps) {
      this.bootstrapAnimation(animatedProps, callback, contextInfo);
    } else {
      callback();
    }
  }

  render() {
    const { children } = this.props;
    const child = React.Children.toArray(children)[0];
    if (child) {
      const cloneProps = { ...child.props };
      // add an 'onRef' func for stateless components
      // i.e. stateless components must implement 'onRef' prop
      // for this to work though
      if (typeof child.type !== 'string') {
        cloneProps.onRef = node => {
          this.node = node;
        };
      }
      cloneProps.ref = node => {
        // might've been set in our 'onRef'
        if (node) {
          this.node = node;
        }
        if (typeof child.ref === 'function') {
          child.ref(node);
        } else if (child.ref) {
          child.ref.current = node;
        }
      };
      return React.cloneElement(child, cloneProps);
    }
    return null;
  }
}

export default AnimatedGroupChild;
